import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import allProjects from "../data/projects.json";
import "../styles/FeaturedProjects.css";

const projects = allProjects.filter(project => !project.hidden);

const projectFromHash = () => {
  if (!window.location.hash.startsWith("#project-")) return null;
  const id = decodeURIComponent(window.location.hash.slice("#project-".length));
  return projects.find(project => project.id === id) || null;
};

const routeForProject = project => `#project-${encodeURIComponent(project.id)}`;

export default function FeaturedProjects() {
  const [selectedProject, setSelectedProject] = useState(projectFromHash);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxZoom, setLightboxZoom] = useState(1);
  const projectGroups = [...new Set(projects.map(project => project.group))];
  const cardRefs = useRef(new Map());
  const activeCardRef = useRef(null);
  const openedFromGridRef = useRef(false);
  const savedScrollRef = useRef(0);
  const caseStudyRef = useRef(null);

  const openProject = (project, card) => {
    savedScrollRef.current = window.scrollY;
    activeCardRef.current = card;
    openedFromGridRef.current = true;
    window.history.pushState({ projectId: project.id }, "", routeForProject(project));
    setSelectedProject(project);
  };

  const restoreProjectsView = () => {
    window.scrollTo(0, savedScrollRef.current);
    activeCardRef.current?.focus();
  };

  const closeProject = () => {
    if (!selectedProject) return;

    if (openedFromGridRef.current && window.location.hash === routeForProject(selectedProject)) {
      window.history.back();
      return;
    }

    openedFromGridRef.current = false;
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}`);
    setSelectedProject(null);
    setLightboxImage(null);
  };

  const selectAdjacentProject = offset => {
    const index = projects.findIndex(project => project.id === selectedProject.id);
    const nextProject = projects[index + offset];
    if (!nextProject) return;

    window.history.replaceState({ projectId: nextProject.id }, "", routeForProject(nextProject));
    setSelectedProject(nextProject);
    setLightboxImage(null);
    setLightboxZoom(1);
    caseStudyRef.current?.scrollTo(0, 0);
    caseStudyRef.current?.querySelector(".case-study-back")?.focus();
  };

  useEffect(() => {
    const handlePopState = () => {
      const hashProject = projectFromHash();
      if (hashProject) {
        setSelectedProject(hashProject);
        setLightboxImage(null);
        return;
      }

      openedFromGridRef.current = false;
      setLightboxImage(null);
      setSelectedProject(null);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (!selectedProject) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => caseStudyRef.current?.querySelector(".case-study-back")?.focus());

    return () => {
      document.body.style.overflow = previousOverflow;
      requestAnimationFrame(restoreProjectsView);
    };
  }, [Boolean(selectedProject)]);

  useEffect(() => {
    if (!selectedProject) return undefined;

    const handleKeyDown = event => {
      if (event.key === "Escape") {
        if (lightboxImage) {
          setLightboxImage(null);
          setLightboxZoom(1);
        } else {
          closeProject();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedProject, lightboxImage]);

  return (
    <section id="projects" className="featured-projects-section">
      <div className="section-header">
        <h2 className="section-title">Projects</h2>
      </div>

      {projectGroups.map(group => (
        <div className="project-group" key={group}>
          <h3 className="project-group-title">{group}</h3>
          <div className="project-grid">
            {projects.filter(project => project.group === group).map(project => (
              <button
                key={project.id}
                ref={element => {
                  if (element) cardRefs.current.set(project.id, element);
                  else cardRefs.current.delete(project.id);
                }}
                type="button"
                className="project-card"
                onClick={event => openProject(project, event.currentTarget)}
                aria-label={`View ${project.title} case study`}
              >
                <ProjectArtwork project={project} className="project-card-image" />
                <span className="project-card-copy">
                  <span className="project-card-title" title={project.title}>{project.title}</span>
                  <span className="project-card-result">{project.result}</span>
                  <span className="project-card-tags">
                    {project.tags.slice(0, 3).map(tag => <span className="project-tag" key={tag}>{tag}</span>)}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}

      {selectedProject && createPortal((
        <div className="project-case-study-backdrop">
          <main
            className="project-case-study"
            role="dialog"
            aria-modal="true"
            aria-labelledby="case-study-title"
            tabIndex={-1}
            ref={caseStudyRef}
          >
            <div className="case-study-topbar">
              <button type="button" className="case-study-back" onClick={closeProject}>
                <span aria-hidden="true">←</span> Back to projects
              </button>
              <button type="button" className="case-study-close" aria-label="Close case study" onClick={closeProject}>
                ×
              </button>
            </div>

            <article className="case-study-content">
              <header className="case-study-header">
                <h1 id="case-study-title">{selectedProject.title}</h1>
                <p className="case-study-result">{selectedProject.result}</p>
                <div className="project-modal-tags" aria-label="Tools used">
                  {selectedProject.tags.map(tag => <span className="project-tag" key={tag}>{tag}</span>)}
                </div>
              </header>

              <button
                type="button"
                className="case-study-hero"
                onClick={() => selectedProject.image && setLightboxImage(selectedProject.image)}
                disabled={!selectedProject.image}
                aria-label={selectedProject.image ? `Open full-screen image for ${selectedProject.title}` : undefined}
              >
                <ProjectArtwork project={selectedProject} className="case-study-hero-artwork" />
                {selectedProject.image && <span className="case-study-image-hint">Open image</span>}
              </button>

              {selectedProject.stats?.length > 0 && (
                <div className="case-study-stats" aria-label="Project statistics">
                  {selectedProject.stats.slice(0, 4).map(stat => (
                    <div className="case-study-stat" key={`${stat.value}-${stat.label}`}>
                      <strong>{stat.value}</strong>
                      <span>{stat.label}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="case-study-sections">
                <section className="case-study-section">
                  <h2>The Problem</h2>
                  <p>{selectedProject.problem || `This project examines ${selectedProject.title} and the decisions behind its key result.`}</p>
                </section>
                <section className="case-study-section">
                  <h2>What I Did</h2>
                  <p>{selectedProject.approach || selectedProject.summary}</p>
                </section>
                <section className="case-study-section">
                  <h2>The Result</h2>
                  <p>{selectedProject.outcome || selectedProject.result}</p>
                </section>
              </div>

              {selectedProject.gallery?.length > 0 && (
                <div className="case-study-gallery">
                  {selectedProject.gallery.map((image, index) => (
                    <button
                      type="button"
                      className="case-study-gallery-image"
                      key={image}
                      onClick={() => setLightboxImage(image)}
                      aria-label={`Open project image ${index + 1} full screen`}
                    >
                      <img src={image} alt={`${selectedProject.title} detail ${index + 1}`} />
                    </button>
                  ))}
                </div>
              )}

              {selectedProject.pdfs?.length > 0 && (
                <div className="project-pdf-list">
                  <h2>Case study PDFs</h2>
                  {selectedProject.pdfs.map(pdf => (
                    <a key={pdf.url} href={pdf.url} target="_blank" rel="noopener noreferrer">{pdf.name} <span aria-hidden="true">↗</span></a>
                  ))}
                </div>
              )}

              <div className="case-study-actions">
                {selectedProject.githubUrl && <a href={selectedProject.githubUrl} target="_blank" rel="noopener noreferrer">GitHub</a>}
                {selectedProject.liveUrl && <a href={selectedProject.liveUrl} target="_blank" rel="noopener noreferrer">Live Dashboard</a>}
                {selectedProject.pdfUrl && <a href={selectedProject.pdfUrl} target="_blank" rel="noopener noreferrer">PDF</a>}
              </div>

              <nav className="case-study-footer" aria-label="Browse projects">
                <button
                  type="button"
                  onClick={() => selectAdjacentProject(-1)}
                  disabled={projects[0].id === selectedProject.id}
                >
                  <span>← Previous project</span>
                  <strong>{projects[projects.findIndex(project => project.id === selectedProject.id) - 1]?.title || ""}</strong>
                </button>
                <button
                  type="button"
                  onClick={() => selectAdjacentProject(1)}
                  disabled={projects[projects.length - 1].id === selectedProject.id}
                >
                  <span>Next project →</span>
                  <strong>{projects[projects.findIndex(project => project.id === selectedProject.id) + 1]?.title || ""}</strong>
                </button>
              </nav>
            </article>
          </main>

          {lightboxImage && (
            <div
              className="case-study-lightbox"
              role="dialog"
              aria-modal="true"
              aria-label="Full-screen project image"
              onMouseDown={event => {
                if (event.target === event.currentTarget) {
                  setLightboxImage(null);
                  setLightboxZoom(1);
                }
              }}
            >
              <button
                type="button"
                className="case-study-lightbox-close"
                aria-label="Close full-screen image"
                onClick={() => { setLightboxImage(null); setLightboxZoom(1); }}
              >
                ×
              </button>
              <div className="case-study-lightbox-controls" aria-label="Image zoom controls">
                <button type="button" aria-label="Zoom out" onClick={() => setLightboxZoom(zoom => Math.max(1, zoom - 0.25))}>−</button>
                <span>{Math.round(lightboxZoom * 100)}%</span>
                <button type="button" aria-label="Zoom in" onClick={() => setLightboxZoom(zoom => Math.min(3, zoom + 0.25))}>+</button>
              </div>
              <div className="case-study-lightbox-image-wrap">
                <img src={lightboxImage} alt={`${selectedProject.title} full-size`} style={{ transform: `scale(${lightboxZoom})` }} />
              </div>
            </div>
          )}
        </div>
      ), document.body)}
    </section>
  );
}

function ProjectArtwork({ project, className }) {
  if (project.image) {
    return <img className={className} src={project.image} alt="" />;
  }

  const initials = project.title
    .split(/\s+/)
    .filter(word => !["vs", "and", "the", "of"].includes(word.toLowerCase()))
    .slice(0, 2)
    .map(word => word[0])
    .join("")
    .toUpperCase();

  return (
    <span className={`${className} project-artwork-placeholder`} aria-hidden="true">
      {initials}
    </span>
  );
}
